import {
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
  provideRouter,
} from '@angular/router';
import { HighContrastMode, HighContrastModeDetector } from '@angular/cdk/a11y';
import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import ar from '@angular/common/locales/ar';
import en from '@angular/common/locales/en';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ar as dateLocale } from 'date-fns/locale';
import { NzConfig, provideNzConfig } from 'ng-zorro-antd/core/config';
import { ar_EG, en_US, NZ_DATE_LOCALE, NZ_I18N } from 'ng-zorro-antd/i18n';
import { environment } from '../environments/environment';
import { routes } from './app-routes';
import { CustomTitleStrategy } from './core/services/CustomTitleStrategy.provider';
import { setGlobalTranslateService } from './app-const';
import { errorInterceptor } from './core/helpers/error.interceptor';
import { jwtInterceptor } from './core/helpers/jwt.interceptor';
import { ApiConfiguration } from './core/backend/api-configuration';

registerLocaleData(ar);
registerLocaleData(en);

const ngZorroConfig: NzConfig = {
  message: { nzTop: 120 },
  notification: { nzTop: 240 },
  form: {
    nzNoColon: true,
  },
};

const noopHighContrastModeDetector = {
  getHighContrastMode: () => HighContrastMode.NONE,
  ngOnDestroy: () => undefined,
  _applyBodyHighContrastModeCssClasses: () => undefined,
};

export function createTranslateLoader(http: HttpClient): TranslateLoader {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([jwtInterceptor, errorInterceptor])),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withViewTransitions(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      withRouterConfig({
        canceledNavigationResolution: 'replace',
        paramsInheritanceStrategy: 'always',
        urlUpdateStrategy: 'deferred',
      })
    ),
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    {
      provide: HighContrastModeDetector,
      useValue: noopHighContrastModeDetector,
    },
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: (translate: TranslateService) => () => {
        setGlobalTranslateService(translate);
      },
      deps: [TranslateService],
      multi: true,
    },
    provideNzConfig(ngZorroConfig),
    {
      provide: NZ_I18N,
      useFactory: () => {
        const savedLang = localStorage.getItem('lang');
        return savedLang === 'en' ? en_US : ar_EG;
      },
    },
    {
      provide: NZ_DATE_LOCALE,
      useValue: dateLocale,
    },
    {
      provide: LOCALE_ID,
      useFactory: () => {
        const savedLang = localStorage.getItem('lang');
        return savedLang === 'en' ? 'en' : 'ar';
      },
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EGP',
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        dateFormat: 'd MMMM yyyy',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    },
    {
      provide: ApiConfiguration,
      useFactory: () => {
        const config = new ApiConfiguration();
        config.rootUrl = environment.api;
        return config;
      },
    },
  ],
};
